/*
  Summit Feedback results (admin).
  Responses grouped by workshop (all respondents shown per workshop),
  participation tracking (permission holders vs responded), AI-generated
  implementation ideas (Anthropic), and a print-friendly layout.
*/
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { get, post, getErrorMessage } from '../api/client';

const displayName = (u, userId) => {
  const name = [u?.firstName, u?.lastName].filter(Boolean).join(' ');
  return name || u?.email || userId;
};

const cardStyle = {
  background: 'var(--cr-bg-warm)',
  border: '1px solid var(--cr-border)',
  borderRadius: '10px',
  padding: '1rem 1.15rem',
  marginBottom: '1rem',
};

const effortColors = { low: '#2e7d32', medium: '#b98a00', high: '#b3402e' };

const PRINT_CSS = `
@media print {
  nav, .no-print, header .back-link { display: none !important; }
  body { background: #fff !important; }
  .print-area { max-width: 100% !important; }
  .sf-card { break-inside: avoid; border: 1px solid #ccc !important; }
  details.sf-workshop { break-inside: avoid; }
  details.sf-workshop > *:not(summary) { display: block; }
  details.sf-workshop summary { list-style: none; }
  details.sf-respondent > *:not(summary) { display: block; }
  details.sf-respondent summary { list-style: none; }
}
`;

const SummitFeedbackResultsPage = () => {
  const { yearId } = useParams();
  const [results, setResults] = useState(null);
  const [participation, setParticipation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aiSummary, setAiSummary] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [aiError, setAiError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, pRes] = await Promise.all([
        get(`/api/summit-feedback/years/${yearId}/results`),
        get(`/api/summit-feedback/years/${yearId}/participation`),
      ]);
      if (rRes.ok) {
        setResults(rRes.data);
        setAiSummary(rRes.data.aiSummary || null);
      } else {
        setError(getErrorMessage(rRes, 'Failed to load results'));
      }
      if (pRes.ok) setParticipation(pRes.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [yearId]);

  useEffect(() => { load(); }, [load]);

  const generateInsights = async () => {
    setGenerating(true);
    setAiError('');
    const { ok, status, data } = await post(`/api/summit-feedback/years/${yearId}/ai-summary`, {});
    setGenerating(false);
    if (ok) setAiSummary(data);
    else setAiError(getErrorMessage({ status, data }, 'Failed to generate insights'));
  };

  if (loading) return <div style={{ padding: '2rem' }}>Loading…</div>;
  if (!results) {
    return (
      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 1rem' }}>
        <PageHeader title="Summit Results" backTo="/admin/summit-feedback" backLabel="← Summit Feedback" />
        <p>{error || 'Results not found.'}</p>
      </div>
    );
  }

  const { year, workshops, template } = results;
  const questionText = Object.fromEntries((template?.questions || []).map((q) => [q.key, q.text]));
  const captured = year.status === 'captured';
  const totalSubmitted = workshops.reduce(
    (sum, w) => sum + w.responses.filter((r) => r.status === 'submitted').length, 0
  );

  return (
    <div className="print-area" style={{ maxWidth: '820px', margin: '0 auto', padding: '0 1rem 6rem 1rem' }}>
      <style>{PRINT_CSS}</style>
      <PageHeader title={`${year.title} — Results`} backTo="/admin/summit-feedback" backLabel="← Summit Feedback" />

      <div className="no-print" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.85rem', fontWeight: 600, borderRadius: '999px', padding: '0.15rem 0.6rem', background: captured ? 'var(--cr-border)' : 'var(--cr-success, #2e7d32)', color: captured ? 'var(--cr-text)' : '#fff' }}>
          {captured ? `Captured ${year.capturedAt ? new Date(year.capturedAt).toLocaleDateString() : ''}` : 'Open — still collecting'}
        </span>
        <span style={{ color: 'var(--cr-text-muted)', fontSize: '0.9rem' }}>{totalSubmitted} submitted responses</span>
        <button type="button" className="btn-outline" onClick={() => window.print()} style={{ marginLeft: 'auto' }}>
          Print
        </button>
      </div>

      {error && <p style={{ color: 'var(--cr-error)' }}>{error}</p>}

      {/* ---- AI implementation ideas ---- */}
      <section className="sf-card" style={cardStyle} aria-labelledby="ai-heading">
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
          <h2 id="ai-heading" style={{ margin: 0, fontSize: '1.15rem', flex: '1 1 auto' }}>Items to consider for implementation</h2>
          <button
            type="button"
            className="btn-primary no-print"
            onClick={generateInsights}
            disabled={generating || !captured || totalSubmitted === 0}
            title={!captured ? 'Capture the year first' : totalSubmitted === 0 ? 'No submitted responses yet' : undefined}
          >
            {generating ? 'Generating… (can take ~30s)' : aiSummary ? 'Regenerate' : 'Generate insights'}
          </button>
        </div>
        {aiSummary?.generatedAt && (
          <p style={{ margin: '0.35rem 0 0', color: 'var(--cr-text-muted)', fontSize: '0.85rem' }}>
            Generated {new Date(aiSummary.generatedAt).toLocaleString()} with {aiSummary.model}
          </p>
        )}
        {aiError && <p style={{ color: 'var(--cr-error)' }}>{aiError}</p>}
        {!aiSummary && !aiError && (
          <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.95rem', marginBottom: 0 }}>
            {captured
              ? 'Click "Generate insights" to have AI analyze all captured feedback and suggest a prioritized list of ideas with their estimated ministry impact.'
              : 'Capture the year to enable AI-generated implementation ideas from the collected feedback.'}
          </p>
        )}
        {aiSummary?.items?.length > 0 && (
          <ol style={{ margin: '1rem 0 0', paddingLeft: '1.25rem' }}>
            {aiSummary.items.map((item, i) => (
              <li key={i} style={{ marginBottom: '0.9rem' }}>
                <p style={{ margin: 0, fontWeight: 600 }}>
                  {item.title}
                  {item.effort && (
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', fontWeight: 600, color: '#fff', background: effortColors[item.effort] || 'var(--cr-border)', borderRadius: '999px', padding: '0.1rem 0.5rem', verticalAlign: 'middle' }}>
                      {item.effort} effort
                    </span>
                  )}
                </p>
                <p style={{ margin: '0.25rem 0' }}>{item.description}</p>
                {item.estimatedImpact && (
                  <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                    <strong>Estimated impact:</strong> {item.estimatedImpact}
                  </p>
                )}
                {item.sourceWorkshops?.length > 0 && (
                  <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--cr-text-muted)' }}>
                    From: {item.sourceWorkshops.join(', ')}
                  </p>
                )}
              </li>
            ))}
          </ol>
        )}
        {aiSummary && (!aiSummary.items || aiSummary.items.length === 0) && aiSummary.rawText && (
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.95rem' }}>{aiSummary.rawText}</pre>
        )}
      </section>

      {/* ---- Participation ---- */}
      {participation && (
        <section className="sf-card" style={cardStyle} aria-labelledby="participation-heading">
          <h2 id="participation-heading" style={{ marginTop: 0, fontSize: '1.15rem' }}>Participation</h2>
          <p style={{ margin: '0 0 0.75rem', color: 'var(--cr-text-muted)', fontSize: '0.9rem' }}>
            {participation.responded.length} of {participation.responded.length + participation.notResponded.length} people with access have responded.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
            <div>
              <h3 style={{ margin: '0 0 0.35rem', fontSize: '0.95rem' }}>Responded ({participation.responded.length})</h3>
              <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.9rem' }}>
                {participation.responded.map((u) => (
                  <li key={u.userId}>
                    {displayName(u, u.userId)}{u.isAdmin ? ' (admin)' : ''} — {u.workshopCount} workshop{u.workshopCount === 1 ? '' : 's'}
                  </li>
                ))}
                {participation.responded.length === 0 && <li style={{ color: 'var(--cr-text-muted)' }}>No one yet</li>}
              </ul>
            </div>
            <div>
              <h3 style={{ margin: '0 0 0.35rem', fontSize: '0.95rem' }}>Not responded ({participation.notResponded.length})</h3>
              <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.9rem' }}>
                {participation.notResponded.map((u) => (
                  <li key={u.userId}>{displayName(u, u.userId)}{u.isAdmin ? ' (admin)' : ''}</li>
                ))}
                {participation.notResponded.length === 0 && <li style={{ color: 'var(--cr-text-muted)' }}>Everyone responded 🎉</li>}
              </ul>
            </div>
          </div>
          {participation.respondedWithoutPermission?.length > 0 && (
            <p style={{ margin: '0.75rem 0 0', fontSize: '0.85rem', color: 'var(--cr-text-muted)' }}>
              Responded but no longer has the permission: {participation.respondedWithoutPermission.map((u) => displayName(u, u.userId)).join(', ')}
            </p>
          )}
        </section>
      )}

      {/* ---- Responses grouped by workshop ---- */}
      <h2 style={{ fontSize: '1.15rem' }}>Feedback by workshop</h2>
      {workshops.map(({ workshop, responses }) => {
        const submitted = responses.filter((r) => r.status === 'submitted');
        const drafts = responses.filter((r) => r.status === 'draft');
        return (
          <details key={workshop._id} className="sf-workshop" open={submitted.length > 0} style={{ ...cardStyle, padding: 0 }}>
            <summary style={{ cursor: 'pointer', padding: '0.85rem 1.15rem', fontWeight: 600 }}>
              {workshop.isOther ? `Write-in: ${workshop.name}` : `Session ${workshop.sessionNumber}: ${workshop.name}`}
              {workshop.isOther && workshop.speaker ? ` (${workshop.speaker})` : ''}
              <span style={{ fontWeight: 400, color: 'var(--cr-text-muted)', fontSize: '0.9rem' }}>
                {' '}— {submitted.length} response{submitted.length === 1 ? '' : 's'}
                {drafts.length > 0 ? ` (+${drafts.length} draft${drafts.length === 1 ? '' : 's'})` : ''}
              </span>
            </summary>
            <div style={{ padding: '0 1.15rem 1rem' }}>
              {responses.length === 0 && <p style={{ color: 'var(--cr-text-muted)' }}>No feedback for this session.</p>}
              {responses.map((r) => (
                <details key={r._id} className="sf-card sf-respondent" style={{ border: '1px solid var(--cr-border)', borderRadius: '8px', marginBottom: '0.75rem', background: 'var(--cr-bg, #fff)' }}>
                  <summary style={{ cursor: 'pointer', padding: '0.65rem 1rem', fontWeight: 600 }}>
                    {displayName(r.user, r.userId)}
                    <span style={{ fontWeight: 400, fontSize: '0.85rem', color: 'var(--cr-text-muted)' }}>
                      {' '}· {r.status === 'submitted'
                        ? `submitted ${r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : ''}`
                        : 'draft (not submitted)'}
                    </span>
                  </summary>
                  <div style={{ padding: '0 1rem 0.75rem' }}>
                    {(template?.questions || []).map((q) => {
                      const answer = (r.answers?.[q.key] || '').trim();
                      if (!answer) return null;
                      return (
                        <div key={q.key} style={{ marginBottom: '0.6rem' }}>
                          <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--cr-text-muted)' }}>{questionText[q.key]}</p>
                          <p style={{ margin: '0.15rem 0 0', whiteSpace: 'pre-wrap' }}>{answer}</p>
                        </div>
                      );
                    })}
                  </div>
                </details>
              ))}
            </div>
          </details>
        );
      })}
      {workshops.length === 0 && <p style={{ color: 'var(--cr-text-muted)' }}>No workshops in this year.</p>}
    </div>
  );
};

export default SummitFeedbackResultsPage;
